import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FaPlus, FaFile, FaDownload, FaCheck, FaTimes, FaEye, FaUserPlus } from 'react-icons/fa';
import translations from '../../translations/de';
import Table from '../../components/Table';
import Button from '../../components/Button';
import Select from '../../components/Select';
import InputSeason from '../../components/InputSeason/InputSeason';
import Paths from '../../constants/paths';
import parkingAPI from '../../api/parking';
import formatPin from '../../helpers/formatPin';
import AssignOwnerModal from './AssignOwnerModal';

// Display name of the app user a pass is assigned to (owner), if any.
const ownerName = (p) =>
    p.user ? [p.user.first_name, p.user.last_name].filter(Boolean).join(' ').trim() || p.user.email || `#${p.user.id}` : '';

// One status dropdown instead of separate tabs (cleaner, extensible).
const STATUS_OPTIONS = [
    { label: translations.all, value: 'all' },
    { label: 'Zugeteilt', value: 'assigned' },
    { label: 'Aktiviert', value: 'activated' },
    { label: 'Nicht aktiviert', value: 'not_activated' },
    { label: 'Verfügbar', value: 'available' },
];

// A pass is "assigned" (zugeteilt) once it has an app user or any holder contact
// detail; a blank pool pass has none and is "verfügbar". Activation is user_id.
const isAssigned = (p) =>
    !!(p.user_id || p.first_name || p.last_name || p.email || p.phone);

// Parkkarten for a single field, shown as a tab on the field detail page.
const FieldParkingPasses = ({ fieldId, fieldName }) => {
    const history = useHistory();
    const [passes, setPasses] = useState([]);
    const [seasonId, setSeasonId] = useState(null);
    const [status, setStatus] = useState('all');
    const [reload, setReload] = useState(0);
    // Pass row whose owner is being assigned (opens the modal).
    const [ownerModalPass, setOwnerModalPass] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const params = { parking_field_id: fieldId };
                if (seasonId) params.season_id = seasonId;
                const res = await parkingAPI.getPasses({ params });
                setPasses(res.data || []);
            } catch (e) {
                setPasses([]);
            }
        };
        load();
    }, [fieldId, seasonId, reload]);

    const filtered = passes.filter((p) => {
        if (status === 'assigned') return isAssigned(p);
        if (status === 'activated') return !!p.user_id;
        if (status === 'not_activated') return isAssigned(p) && !p.user_id;
        if (status === 'available') return !isAssigned(p);
        return true;
    });

    // Launch create/import for THIS field (pre-selected via route state).
    const goCreate = (path) =>
        history.push({ pathname: path, state: { parkingFieldId: fieldId, parkingFieldName: fieldName } });

    // Written straight off `filtered`, the same array the table renders, so the
    // file always matches what is on screen — including the status filter.
    //
    // It carries the Besitzer columns because the table shows that column and
    // the file did not: an owner assigned in the admin was visible in the
    // overview and missing from the download, which read as a stale export
    // rather than a missing column. Status is one readable word instead of a
    // yes/no, matching the dropdown above the table.
    const exportCsv = () => {
        const statusLabel = (p) => {
            if (p.user_id) return 'Aktiviert';
            if (isAssigned(p)) return 'Nicht aktiviert';
            return 'Verfügbar';
        };

        const header = [
            'ID',
            translations.parking_code,
            translations.type,
            'Status',
            'Vorname',
            'Nachname',
            'Email',
            'Telefon',
            'Besitzer',
            'Besitzer Email',
        ];
        const rows = filtered.map((p) => [
            p.id,
            formatPin(p.assign_token),
            p.type,
            statusLabel(p),
            p.first_name ?? '',
            p.last_name ?? '',
            p.email ?? '',
            p.phone ?? '',
            ownerName(p),
            p.user?.email ?? '',
        ]);
        const csv = [header, ...rows]
            .map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
            .join('\n');
        const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `parking-passes-${fieldName ?? fieldId}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const columns = [
        { Header: 'ID', accessor: 'id', Cell: ({ value }) => <div>{value}</div> },
        { Header: translations.parking_code, accessor: 'code', Cell: ({ value }) => <div>{value}</div> },
        {
            Header: translations.assigned,
            accessor: 'assigned',
            Cell: ({ value }) => (
                <div>{value ? <FaCheck className="confirm" /> : <FaTimes className="error" />}</div>
            ),
        },
        { Header: 'Kontakt', accessor: 'contact', Cell: ({ value }) => <div>{value}</div> },
        { Header: 'Besitzer', accessor: 'owner', Cell: ({ value }) => <div>{value || '-'}</div> },
        {
            Header: '',
            accessor: 'action',
            Cell: ({ row }) => (
                <div className="button-cell">
                    <Button
                        title="Zuteilen"
                        onClick={() =>
                            setOwnerModalPass(passes.find((p) => p.id === row.original.id))
                        }
                    >
                        <FaUserPlus />
                    </Button>
                    <Button
                        title={translations.show}
                        onClick={() =>
                            history.push(Paths.PARKING_PASS.replace(':pass_id', row.original.id))
                        }
                    >
                        <FaEye />
                    </Button>
                </div>
            ),
        },
    ];

    return (
        <div style={{ width: '100%' }}>
            <div className="tab-controls">
                <InputSeason selectedSeason={seasonId} setSelectedSeason={setSeasonId} />
                <Select
                    label="Status"
                    defaultValue={STATUS_OPTIONS[0]}
                    options={STATUS_OPTIONS}
                    isSearchable={false}
                    onChange={(e) => setStatus(e.value)}
                />
            </div>
            <div className="tab-controls">
                <Button
                    label={translations.create}
                    onClick={() => goCreate(Paths.NEW_PARKING_PASS)}
                    style={{ marginRight: '15px' }}
                >
                    <FaPlus style={{ marginRight: '5px' }} />
                </Button>
                <Button
                    label="Import CSV (QR)"
                    onClick={() => goCreate(Paths.PARKING_IMPORT_CSV)}
                    style={{ marginRight: '15px' }}
                >
                    <FaFile style={{ marginRight: '5px' }} />
                </Button>
                <Button
                    label="Import Liste"
                    onClick={() => goCreate(Paths.PARKING_IMPORT_PASSES)}
                    style={{ marginRight: '15px' }}
                >
                    <FaFile style={{ marginRight: '5px' }} />
                </Button>
                <Button label={translations.export} onClick={exportCsv}>
                    <FaDownload style={{ marginRight: '5px' }} />
                </Button>
            </div>
            <Table
                tableName={translations.parking_passes}
                columns={columns}
                data={filtered.map((p) => ({
                    id: p.id,
                    code: formatPin(p.assign_token),
                    assigned: !!p.user_id,
                    contact: [p.first_name, p.last_name].filter(Boolean).join(' ') || '-',
                    owner: ownerName(p),
                }))}
            />

            {ownerModalPass && (
                <AssignOwnerModal
                    passId={ownerModalPass.id}
                    currentOwner={ownerName(ownerModalPass)}
                    onClose={() => setOwnerModalPass(null)}
                    onAssigned={() => {
                        setOwnerModalPass(null);
                        setReload((r) => r + 1);
                    }}
                />
            )}
        </div>
    );
};

export default FieldParkingPasses;
